"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { ArrowLeft, CheckCircle2, Mic, Square, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SpeakerButton } from "@/components/speaker-button";
import { useTTS } from "@/lib/use-tts";
import { useSpeechToText } from "@/lib/use-speech-to-text";
import { useTranslations } from "@/lib/use-translations";
import { useLanguage } from "@/lib/language-context";
import { matchPhraseWords, type PhraseMatchResult } from "@/lib/spelling-comparison";
import type { TopicVocabulary } from "@/lib/dialog-data";
import type { LocaleCode } from "@/lib/api/client";

type KaraokeState = "show" | "recording" | "processing" | "result" | "complete";

interface KaraokePracticeProps {
  vocabulary: TopicVocabulary;
  language?: LocaleCode;
  onBack: () => void;
}

export function KaraokePractice({ vocabulary, onBack }: KaraokePracticeProps) {
  const expressions = vocabulary.expressions;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [state, setState] = useState<KaraokeState>("show");
  const [matchResults, setMatchResults] = useState<PhraseMatchResult[]>([]);
  const [revealedCount, setRevealedCount] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [heardText, setHeardText] = useState("");
  const t = useTranslations();
  const { targetLanguage } = useLanguage();
  const langCode = targetLanguage.code;
  const tts = useTTS({ lang: langCode });
  const animationTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const currentExpression = expressions[currentIndex] as typeof expressions[number] | undefined;

  const handleSTTResult = useCallback(
    (transcript: string) => {
      if (!currentExpression) return;
      setHeardText(transcript);
      const results = matchPhraseWords(currentExpression.expression, transcript);
      setMatchResults(results);
      setRevealedCount(0);
      setState("result");

      const allMatched = results.every((r) => r.matched);
      if (allMatched) {
        setCorrectCount((c) => c + 1);
      }
    },
    [currentExpression]
  );

  const stt = useSpeechToText({
    language: langCode,
    onResult: handleSTTResult,
  });

  // Recover from STT errors (processing done but no result)
  useEffect(() => {
    if (state === "processing" && !stt.isProcessing && !stt.isRecording) {
      setState("show");
    }
  }, [state, stt.isProcessing, stt.isRecording]);

  // Animate word reveal
  useEffect(() => {
    if (state !== "result" || matchResults.length === 0) return;

    if (revealedCount < matchResults.length) {
      animationTimerRef.current = setTimeout(() => {
        setRevealedCount((c) => c + 1);
      }, 300);
    }

    return () => {
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    };
  }, [state, revealedCount, matchResults.length]);

  const handleNext = () => {
    if (currentIndex + 1 >= expressions.length) {
      setState("complete");
    } else {
      setCurrentIndex((i) => i + 1);
      setState("show");
      setMatchResults([]);
      setRevealedCount(0);
      setHeardText("");
    }
  };

  const handleTryAgain = () => {
    setState("show");
    setMatchResults([]);
    setRevealedCount(0);
    setHeardText("");
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setCorrectCount(0);
    setState("show");
    setMatchResults([]);
    setRevealedCount(0);
    setHeardText("");
  };

  const handleToggleRecording = () => {
    if (stt.isRecording) {
      setState("processing");
      stt.stopRecording();
    } else {
      setState("recording");
      stt.startRecording();
    }
  };

  if (expressions.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No expressions to practice
      </div>
    );
  }

  // Complete screen
  if (state === "complete") {
    return (
      <div className="p-4 space-y-6 max-w-lg mx-auto">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-xl">
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div className="text-center space-y-4 pt-8">
          <CheckCircle2 className="w-16 h-16 text-purple-500 mx-auto" />
          <h2 className="text-2xl font-bold">{t.learning.practiceComplete || "Practice Complete!"}</h2>
          <p className="text-lg text-muted-foreground">
            {(t.learning.wordsCorrectCount || "{correct} of {total} correct")
              .replace("{correct}", String(correctCount))
              .replace("{total}", String(expressions.length))}
          </p>

          <div className="flex gap-3 justify-center pt-4">
            <Button variant="outline" onClick={handleRestart}>
              {t.learning.practiceAgain || "Practice Again"}
            </Button>
            <Button onClick={onBack}>
              {t.learning.done || "Done"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentExpression) return null;

  const allRevealed = revealedCount >= matchResults.length && matchResults.length > 0;
  const isPassing = matchResults.length > 0 && matchResults.every((r) => r.matched);

  return (
    <div className="p-4 space-y-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-xl">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t.learning.practiceKaraoke || "Karaoke"}
          </h3>
          <div className="text-xs text-muted-foreground">
            {currentIndex + 1} / {expressions.length}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-purple-500 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex) / expressions.length) * 100}%` }}
        />
      </div>

      {/* Expression display */}
      <div className="text-center space-y-3 py-6">
        {state === "result" ? (
          /* Karaoke reveal */
          <div className="space-y-4">
            <div className="flex flex-wrap justify-center gap-1.5">
              {matchResults.map((result, idx) => {
                const isRevealed = idx < revealedCount;
                return (
                  <span
                    key={idx}
                    className={`
                      px-2 py-1 rounded text-lg font-medium transition-all duration-300
                      ${!isRevealed
                        ? "text-muted-foreground/30"
                        : result.matched
                          ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10"
                          : "text-red-600 dark:text-red-400 bg-red-500/10"
                      }
                    `}
                  >
                    {result.word}
                  </span>
                );
              })}
            </div>

            {heardText && (
              <p className="text-sm text-muted-foreground">
                {t.learning.weHeard || "We heard:"} <span className="font-medium text-foreground">{heardText}</span>
              </p>
            )}

            {allRevealed && (
              <div className="flex gap-3 justify-center pt-2 animate-fade-up">
                {!isPassing && (
                  <Button variant="outline" onClick={handleTryAgain}>
                    {t.learning.tryAgain || "Try Again"}
                  </Button>
                )}
                <Button onClick={handleNext}>
                  {t.learning.nextWord || "Next"}
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Show expression */
          <>
            <div className="text-2xl font-bold text-foreground">
              {currentExpression.expression}
            </div>
            {currentExpression.vocalizationTip && (
              <div className="text-base text-muted-foreground">
                {currentExpression.vocalizationTip}
              </div>
            )}
            <div className="text-sm text-muted-foreground/70">
              {currentExpression.translation}
            </div>
            <div className="flex justify-center">
              <SpeakerButton
                state={tts.state}
                onClick={() => tts.speak(currentExpression.expression)}
                size="lg"
              />
            </div>
          </>
        )}
      </div>

      {/* Mic button */}
      {(state === "show" || state === "recording" || state === "processing") && (
        <div className="text-center space-y-3">
          <button
            onClick={handleToggleRecording}
            disabled={state === "processing"}
            className={`
              w-20 h-20 rounded-full mx-auto flex items-center justify-center transition-all shadow-lg
              ${state === "recording"
                ? "bg-destructive text-destructive-foreground scale-110"
                : state === "processing"
                  ? "bg-muted text-muted-foreground cursor-wait"
                  : "bg-purple-500 text-white hover:scale-105 active:scale-95"
              }
            `}
          >
            {state === "processing" ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : state === "recording" ? (
              <Square className="w-8 h-8 fill-current" />
            ) : (
              <Mic className="w-8 h-8" />
            )}
          </button>

          {state === "recording" && (
            <div className="space-y-1">
              <p className="text-base font-medium text-foreground">
                {t.generator?.tapToStop || "Tap to stop"}
              </p>
              <div className="flex items-center justify-center gap-2 text-destructive animate-pulse">
                <div className="w-2 h-2 rounded-full bg-destructive animate-ping" />
                <span className="text-sm font-medium">{t.generator?.recording || "Recording..."}</span>
              </div>
            </div>
          )}

          {state === "processing" && (
            <p className="text-sm text-muted-foreground">
              {t.learning.transcribing || "Transcribing..."}
            </p>
          )}

          {state === "show" && (
            <p className="text-sm text-muted-foreground">
              {t.generator?.tapAndTalk || "Tap and talk"}
            </p>
          )}

          {stt.error && state === "show" && (
            <p className="text-xs text-destructive">{stt.error}</p>
          )}
        </div>
      )}
    </div>
  );
}
